import * as THREE from 'three';
import * as CANNON from 'cannon';

export class Map {
    constructor(assetLoader) {
        this.assetLoader = assetLoader;
        this.mapObject = new THREE.Group();
        this.physicsObjects = [];
        // Initialize spawn position with default values
        this.spawnPosition = new THREE.Vector3(0, 2, 0); // Default spawn position (slightly elevated)
    }

    async loadMap(mapPath, physicsWorld) {
        try {
            console.log(`Loading map from ${mapPath}...`);
            const map = await this.assetLoader.loadModel(mapPath);
            
            // Process shadows before adding to the scene
            this.configureObjectShadows(map);
            
            // Add to the map group after shadow configuration
            this.mapObject.add(map);
            
            // Process all objects in the map
            this.processMapObjects(map, physicsWorld);
            
            return true;
        } catch (error) {
            console.error(`Error loading map ${mapPath}:`, error);
            return false;
        }
    }
    
    configureObjectShadows(object) {
        // Apply shadow settings to all objects and sub-objects recursively
        object.traverse((node) => {
            // Skip shadow configuration for collision-only objects
            if (node.name && node.name.includes('col')) {
                return;
            }
            
            if (node.isMesh) {
                node.castShadow = true;
                node.receiveShadow = true;
                
                // Update material properties to properly support shadows
                if (node.material) {
                    this.configureMaterialForShadows(node.material);
                }
            } else if (node.isLight) {
                // If any lights are in the imported model, make them cast shadows too
                node.castShadow = true;
                
                // Configure shadow properties for lights
                if (node.shadow) {
                    node.shadow.mapSize.width = 2048;
                    node.shadow.mapSize.height = 2048;
                    node.shadow.bias = -0.0005;
                }
            }
        });
    }
    
    configureMaterialForShadows(material) {
        if (Array.isArray(material)) {
            // Handle multi-materials
            material.forEach(mat => {
                if (mat) {
                    mat.shadowSide = THREE.FrontSide;
                    mat.needsUpdate = true;
                }
            });
        } else {
            // Single material
            material.shadowSide = THREE.FrontSide;
            material.needsUpdate = true;
            
            // Additional settings for better shadows
            material.transparent = material.transparent || false;
            
            // For transparent materials, ensure they cast shadows correctly
            if (material.transparent && material.opacity < 1.0) {
                material.depthWrite = true;
            }
        }
    }

    processMapObjects(mapObject, physicsWorld) {
        mapObject.traverse((child) => {
            // Create collision boxes for objects with "Box" or "Trimesh" in their name
            if (child.name.includes('Box') || child.name.includes('Trimesh')) {
                console.log(`Creating collision box for ${child.name}`);
                this.createTrimeshCollision(child, physicsWorld);
            }
            
            // Handle collision-only objects (with "col" in their name)
            if (child.name.includes('col')) {
                console.log(`Creating collision-only object for ${child.name}`);
                this.createTrimeshCollision(child, physicsWorld);
                
                // Make the mesh invisible by removing it from its parent
                if (child.parent) {
                    child.visible = false;
                }
            }
            
            // Check for start position markers
            if (child.name.includes('Start')) {
                console.log(`Found start position at ${child.name}`);
                // Get the world position of the start marker
                const position = new THREE.Vector3();
                child.getWorldPosition(position);
                // Set the spawn position
                this.spawnPosition.copy(position);
                // Add a small y-offset to prevent clipping with the ground
                this.spawnPosition.y += 1;
                console.log(`Spawn position set to: (${this.spawnPosition.x}, ${this.spawnPosition.y}, ${this.spawnPosition.z})`);
            }
        });
    }

    createTrimeshCollision(mesh, physicsWorld) {
        if (!mesh.geometry) {
            console.warn(`No geometry found for ${mesh.name}`);
            return;
        }
        
        // Get the position, rotation, and scale from the mesh
        const position = new THREE.Vector3();
        const quaternion = new THREE.Quaternion();
        const scale = new THREE.Vector3();
        
        mesh.updateMatrixWorld();
        mesh.matrixWorld.decompose(position, quaternion, scale);

        // Create a trimesh shape from the mesh's geometry
        const vertices = mesh.geometry.attributes.position.array;
        const indices = [];
        
        // If the geometry has an index buffer, use it
        if (mesh.geometry.index) {
            for (let i = 0; i < mesh.geometry.index.count; i++) {
                indices.push(mesh.geometry.index.array[i]);
            }
        } else {
            // Otherwise, create indices for every three vertices (for triangles)
            for (let i = 0; i < vertices.length / 3; i++) {
                indices.push(i);
            }
        }
        
        // Convert THREE.js vertices to CANNON.js format (flatten the array)
        const cannonVertices = [];
        for (let i = 0; i < vertices.length; i += 3) {
            cannonVertices.push(
                vertices[i] * scale.x,
                vertices[i + 1] * scale.y,
                vertices[i + 2] * scale.z
            );
        }
        
        // Create the trimesh shape
        const trimeshShape = new CANNON.Trimesh(cannonVertices, indices);
        
        // Create the rigid body with the trimesh shape
        const body = new CANNON.Body({ mass: 0 }); // mass: 0 for static object
        body.addShape(trimeshShape);
        
        // Set the position and rotation of the body to match the mesh
        body.position.set(position.x, position.y, position.z);
        body.quaternion.set(quaternion.x, quaternion.y, quaternion.z, quaternion.w);
        
        // Add the body to the physics world
        physicsWorld.addBody(body);
        
        // Store a reference to the physics object for later use
        this.physicsObjects.push({
            mesh: mesh,
            body: body
        });
    }

    getObject() {
        return this.mapObject;
    }
    
    // Getter method for spawn position
    getSpawnPosition() {
        return this.spawnPosition;
    }
}
